/*
  Samashti student sign-in.

  Two rules shape this file:

  1. **One failure message.** Whether the Student ID was wrong, the phone was
     wrong, the student does not exist, or the attempt was rate-limited, the
     visitor sees exactly the same sentence. Anything else would let someone
     probe the roster by watching which combinations produce which error.

  2. **Always home.** A successful sign-in goes to the 6Hats homepage — not back
     to this page, not to a school landing page. The Uniforms and Books links
     appearing in the header are the confirmation.

  Nothing typed here is stored. The Student ID and phone are posted once and then
  discarded with the form; only the opaque session token the Worker returns is
  kept, in sessionStorage, so it does not survive the tab.
*/
(function () {
  "use strict";

  var TOKEN_KEY = "sannitha:token";

  var GENERIC_ERROR =
    "We couldn't verify those details. Please check the Student ID and registered guardian mobile number.";
  var UNAVAILABLE_ERROR =
    "Student sign-in is temporarily unavailable. Please try again shortly.";

  var root = document.querySelector("[data-samashti-login]");
  if (!root) return;

  var form = root.querySelector("[data-samashti-login-form]");
  var errorNode = root.querySelector("[data-samashti-login-error]");
  var submit = root.querySelector("[data-samashti-login-submit]");
  var endpoint = (root.getAttribute("data-endpoint") || "").replace(/\/+$/, "");
  var homeUrl = root.getAttribute("data-home-url") || "/";

  function showError(message) {
    if (!errorNode) return;
    errorNode.textContent = message;
    errorNode.hidden = false;
    errorNode.setAttribute("role", "alert");
  }

  function clearError() {
    if (!errorNode) return;
    errorNode.hidden = true;
    errorNode.textContent = "";
  }

  function readToken() {
    try {
      var value = window.sessionStorage.getItem(TOKEN_KEY);
      return /^[a-f0-9]{64}$/.test(value || "") ? value : null;
    } catch (error) {
      return null;
    }
  }

  function writeToken(token) {
    try {
      window.sessionStorage.setItem(TOKEN_KEY, token);
    } catch (error) {
      /* Private browsing: the session simply will not survive a reload. */
    }
  }

  /**
   * Where to go after signing in.
   *
   * The homepage, always — the client was explicit about that. A `return`
   * parameter set by the route gate is honoured only when it is a same-origin
   * path, so the sign-in page can never be turned into an open redirect, and
   * only when it is a school route the student was actually stopped from
   * reaching.
   */
  function destination() {
    try {
      var requested = new URL(window.location.href).searchParams.get("return");
      if (
        requested &&
        requested.charAt(0) === "/" &&
        requested.charAt(1) !== "/" &&
        /^\/(collections|products)\//.test(requested)
      ) {
        return requested;
      }
    } catch (error) {
      /* Fall through to the homepage. */
    }
    return homeUrl;
  }

  /**
   * An already-signed-in student has no business on the sign-in page. Rather
   * than trust the stored token, it is validated first — a stale one leaves the
   * form in place instead of bouncing the visitor to a store they cannot use.
   */
  function redirectIfAlreadySignedIn() {
    if (!readToken() || !endpoint) return;

    fetch(endpoint + "/auth/validate", {
      method: "POST",
      headers: {
        "content-type": "application/json",
        authorization: "Bearer " + readToken(),
      },
      body: "{}",
    })
      .then(function (response) {
        if (response.status === 200) window.location.replace(destination());
      })
      .catch(function () {
        /* Leave the form; the runtime will clear the dead token. */
      });
  }

  if (form) {
    form.addEventListener("submit", function (event) {
      event.preventDefault();
      clearError();

      if (!endpoint) {
        showError(UNAVAILABLE_ERROR);
        return;
      }

      var studentId = String(form.elements.studentId.value || "").trim();
      var phone = String(form.elements.phone.value || "").trim();

      if (!studentId || !phone) {
        // Same message as a wrong credential: an empty field must not be
        // distinguishable from an incorrect one.
        showError(GENERIC_ERROR);
        return;
      }

      if (submit) {
        submit.disabled = true;
        submit.setAttribute("aria-busy", "true");
      }

      fetch(endpoint + "/auth/login", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ studentId: studentId, phone: phone }),
      })
        .then(function (response) {
          return response
            .json()
            .catch(function () {
              return {};
            })
            .then(function (payload) {
              return { status: response.status, body: payload };
            });
        })
        .then(function (result) {
          if (result.status === 200 && result.body && result.body.token) {
            writeToken(result.body.token);
            // The credential never touches storage, a log, or the URL. Clearing
            // the form means a back-navigation cannot re-expose it either.
            form.reset();
            window.location.assign(destination());
            return;
          }

          // 401 and 429 are deliberately indistinguishable here. A separate
          // "too many attempts" message would confirm that the earlier guesses
          // had been evaluated against a real student.
          if (result.status === 401 || result.status === 429) {
            showError(GENERIC_ERROR);
          } else {
            showError(UNAVAILABLE_ERROR);
          }
        })
        .catch(function () {
          showError(UNAVAILABLE_ERROR);
        })
        .then(function () {
          if (submit) {
            submit.disabled = false;
            submit.removeAttribute("aria-busy");
          }
        });
    });
  }

  redirectIfAlreadySignedIn();
})();
